import { Stack } from '@mui/material';
import { TopBarRenderer } from './TopBarRenderer';
import { BaseLayoutProps, LayoutTableType } from './types';
import { CustomColumnsTableContextProvider } from '../../../common/table/custom-columns-table/CustomColumnsTableContext';
import { DataTableId } from '../../../common/table/utils';
import { TabsRenderer } from './TabsRenderer';
import { CardRenderer } from './CardRenderer';
import { lazy, Suspense } from 'react';
import { CenteredCircularProgress } from '../../../common/CenteredCircularProgress';

const TableRenderer = lazy(() => import('./TableRenderer'));

export const BaseLayout = ({
  isExpandedMode,
  columns,
  rows,
  pageType,
  crmOrgId,
  topBarProps,
  tableProps,
  tabsProps,
  header,
  dataTestId,
}: BaseLayoutProps) => {
  const sidePadding = 16;
  if (isExpandedMode && !tableProps?.tableId) {
    throw new Error('tableId is required');
  }
  return (
    <CustomColumnsTableContextProvider
      value={{
        columns,
        rows,
        tableId: tableProps?.tableId ?? ('' as DataTableId), // if isExpandedMode is false, tableId is not used
      }}
    >
      <Stack
        className="base-layout"
        data-testid={dataTestId}
        gap={isExpandedMode ? 3 : 2}
        flex={1}
        overflow="hidden"
        height="100%"
      >
        {header}
        <Stack
          height="100%"
          pr={isExpandedMode ? sidePadding + 'px' : 0}
          pl={isExpandedMode ? sidePadding + 'px' : 0}
          gap={isExpandedMode ? 3 : 0}
          overflow="hidden"
        >
          {tabsProps && isExpandedMode && <TabsRenderer {...tabsProps} />}
          <TopBarRenderer
            {...topBarProps}
            isExpandedMode={isExpandedMode}
            itemsCount={rows?.length ?? 0}
            allowColumnsManipulation={[
              LayoutTableType.virtualizedTableWithCustomColumns,
              LayoutTableType.customColumnsTable,
            ].includes(tableProps?.tableType ?? LayoutTableType.table)}
          />
          {isExpandedMode && tableProps && (
            <Suspense fallback={<CenteredCircularProgress />}>
              <Stack height="100%" pb={2}>
                <TableRenderer columns={columns} rows={rows} {...tableProps} />
              </Stack>
            </Suspense>
          )}
          {!isExpandedMode && <CardRenderer items={rows} pageType={pageType} crmOrgId={crmOrgId} />}
        </Stack>
      </Stack>
    </CustomColumnsTableContextProvider>
  );
};
